import { c as _c } from "react/compiler-runtime";
/**
 * Payer-related data fetchers
 */
import { QueryClient, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@/src/context/user-context";
import { toast } from "@/src/hooks/use-toast";
import { getInsuranceMappingPayerIds, payerMatchesInsuranceMappingAllowlist } from "@/src/lib/insurance-mapping-payer-ids";
import { logger } from "@/src/lib/logging/logger";
import { Payer } from "@/src/models/payer";
import { createOrUpdatePayer, updatePayer } from "@/src/repo/payer";
import { getAllPayers } from "@/src/requests/payer";
export const PAYERS_CACHE_KEY = "payers";
export function usePayers() {
  const $ = _c(10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: [PAYERS_CACHE_KEY],
      queryFn: getAllPayers,
      retry: false,
      staleTime: Infinity
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const query = useQuery(t0);
  let t1;
  if ($[1] !== query.data) {
    t1 = query.data || [];
    $[1] = query.data;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== query.error || $[4] !== query.isError || $[5] !== query.isFetching || $[6] !== query.isLoading || $[7] !== query.refetch || $[8] !== t1) {
    t2 = {
      payers: t1,
      isFetching: query.isFetching,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      refetch: query.refetch
    };
    $[3] = query.error;
    $[4] = query.isError;
    $[5] = query.isFetching;
    $[6] = query.isLoading;
    $[7] = query.refetch;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export function useAvailablePayers(currentCustomerPayer) {
  const $ = _c(11);
  const {
    payers,
    isFetching,
    isLoading,
    isError,
    error,
    refetch
  } = usePayers();
  const {
    group
  } = useUser();
  let t0;
  if ($[0] !== currentCustomerPayer || $[1] !== group || $[2] !== payers) {
    bb0: {
      if (!group?.subdomain) {
        t0 = payers;
        break bb0;
      }
      const allowedPayerIds = getInsuranceMappingPayerIds(group);
      if (!allowedPayerIds) {
        t0 = payers;
        break bb0;
      }
      t0 = payers.filter(payer => payerMatchesInsuranceMappingAllowlist(payer, allowedPayerIds, currentCustomerPayer));
    }
    $[0] = currentCustomerPayer;
    $[1] = group;
    $[2] = payers;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const filteredPayers = t0;
  let t1;
  if ($[4] !== error || $[5] !== filteredPayers || $[6] !== isError || $[7] !== isFetching || $[8] !== isLoading || $[9] !== refetch) {
    t1 = {
      payers: filteredPayers,
      isFetching,
      isLoading,
      isError,
      error,
      refetch
    };
    $[4] = error;
    $[5] = filteredPayers;
    $[6] = isError;
    $[7] = isFetching;
    $[8] = isLoading;
    $[9] = refetch;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
export const updatePayersQueryData = (queryClient: QueryClient, updatedPayer: Payer) => {
  queryClient.setQueryData([PAYERS_CACHE_KEY], (oldData: Payer[] = []) => {
    const current = oldData ?? [];
    const exists = current.some(payer => payer.id === updatedPayer.id);
    if (exists) {
      return current.map(payer => payer.id === updatedPayer.id ? updatedPayer : payer);
    }
    return [updatedPayer, ...current];
  });
  if (updatedPayer.id) {
    queryClient.setQueryData([PAYERS_CACHE_KEY, updatedPayer.id], updatedPayer);
  }
};
export function usePayerUpdateMutation() {
  const $ = _c(7);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== queryClient) {
    t0 = async payer_0 => {
      await queryClient.cancelQueries({
        queryKey: [PAYERS_CACHE_KEY]
      });
      const previousPayers = queryClient.getQueryData([PAYERS_CACHE_KEY]) ?? [];
      if (previousPayers.find(p => p.id === payer_0.id)) {
        updatePayersQueryData(queryClient, payer_0);
      }
      return {
        previousPayers
      };
    };
    t1 = (error, variables, context) => {
      logger.error("Error updating payer");
      if (context) {
        queryClient.setQueryData([PAYERS_CACHE_KEY], context.previousPayers);
      }
      toast({
        title: "Failed to save payer",
        description: "There was an error saving the payer. Please try again.",
        variant: "destructive"
      });
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t0 || $[5] !== t1) {
    t3 = {
      mutationFn: _temp,
      onMutate: t0,
      onError: t1,
      meta: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useMutation(t3);
}
async function _temp(payer) {
  const result = await updatePayer(payer.id, payer);
  if (!result) {
    throw new Error("Failed to update payer");
  }
  return result;
}
export function usePayerCreateOrUpdateMutation() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  let t2;
  if ($[0] !== queryClient) {
    t0 = async payer_0 => {
      await queryClient.cancelQueries({
        queryKey: [PAYERS_CACHE_KEY]
      });
      const previousPayers = queryClient.getQueryData([PAYERS_CACHE_KEY]) ?? [];
      updatePayersQueryData(queryClient, payer_0);
      return {
        previousPayers
      };
    };
    t1 = savedPayer => {
      updatePayersQueryData(queryClient, savedPayer);
    };
    t2 = (error, _variables, context) => {
      logger.error("Error creating payer");
      if (context) {
        queryClient.setQueryData([PAYERS_CACHE_KEY], context.previousPayers);
      }
      toast({
        title: "Failed to create payer",
        description: "There was an error creating the payer. Please try again.",
        variant: "destructive"
      });
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t4 = {
      mutationFn: _temp2,
      onMutate: t0,
      onSuccess: t1,
      onError: t2,
      meta: t3
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useMutation(t4);
}
async function _temp2(payer) {
  const result = await createOrUpdatePayer(payer);
  if (!result) {
    throw new Error("Failed to create or update payer");
  }
  return result;
}